import { useEffect, useRef, useState } from 'react';

const stats = [
  {
    value: 500,
    suffix: '+',
    label: 'Casinos Reviewed',
    icon: 'ri-building-fill',
    iconBg: 'from-orange-500 to-red-500',
    accent: 'text-orange-600',
  },
  {
    value: 2,
    suffix: 'M+',
    label: 'Active Users',
    icon: 'ri-user-fill',
    iconBg: 'from-blue-500 to-indigo-500',
    accent: 'text-blue-600',
  },
  {
    value: 13,
    suffix: '+',
    label: 'Years Experience',
    icon: 'ri-time-fill',
    iconBg: 'from-violet-500 to-purple-500',
    accent: 'text-violet-600',
  },
  {
    value: 98,
    suffix: '%',
    label: 'Satisfaction Rate',
    icon: 'ri-star-fill',
    iconBg: 'from-emerald-500 to-green-500',
    accent: 'text-emerald-600',
  },
];

export default function TrustIndicators() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [animatedValues, setAnimatedValues] = useState<number[]>(stats.map(() => 0));
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const sectionEl = sectionRef.current;
    if (!sectionEl || hasAnimated) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry.isIntersecting) return;

        setHasAnimated(true);
        const startTime = performance.now();
        const durationMs = 1400;

        const tick = (now: number) => {
          const progress = Math.min((now - startTime) / durationMs, 1);
          const easedProgress = 1 - Math.pow(1 - progress, 3);

          setAnimatedValues(
            stats.map((stat) => Math.round(stat.value * easedProgress))
          );

          if (progress < 1) {
            requestAnimationFrame(tick);
          }
        };

        requestAnimationFrame(tick);
        observer.disconnect();
      },
      { threshold: 0.3 }
    );

    observer.observe(sectionEl);
    return () => observer.disconnect();
  }, [hasAnimated]);

  const partners = [
    { name: 'CQ9', logo: '/partner-logos/cq9.png' },
    { name: 'Microgaming', logo: '/partner-logos/microgaming.png' },
    { name: 'JILI', logo: '/partner-logos/jili.png' },
    { name: 'JDB', logo: '/partner-logos/jdb.png' },
    { name: 'YGR', logo: '/partner-logos/ygr.png' },
    { name: 'Spade Gaming', logo: '/partner-logos/spade-gaming.png' },
    { name: 'VPower', logo: '/partner-logos/vpower.png' },
    { name: 'WF Gaming', logo: '/partner-logos/wf-gaming.png' }
  ];

  return (
    <section ref={sectionRef} className="py-10 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 md:mb-14">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Trusted by millions of Australians</h2>
          <p className="md:text-lg text-md text-gray-600">Join the community that relies on our expert guidance</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-16">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 md:p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className={`w-12 h-12 bg-gradient-to-br ${stat.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <i className={`${stat.icon} text-2xl text-white`}></i>
                </div>
                <span className={`text-xs font-semibold ${stat.accent} bg-gray-50 border border-gray-200 rounded-full px-2.5 py-1`}>
                  Verified
                </span>
              </div>

              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 leading-none">
                {animatedValues[index]}
                {stat.suffix}
              </div>
              <div className="mt-2 text-sm md:text-base text-gray-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-3xl shadow-xl md:p-12 p-6 border border-gray-100">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">Our Trusted Partners</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6 items-center">
            {partners.map((partner, index) => (
              <div key={index} className="text-center">
                <div className="bg-gray-100 rounded-xl p-4 hover:bg-gray-200 transition-all cursor-pointer h-24 flex items-center justify-center">
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="max-h-14 w-auto object-contain"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-8">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-100">
            <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-4">
              <i className="ri-shield-check-fill text-2xl text-white"></i>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">100% Secure</h4>
            <p className="text-gray-600">All recommended casinos use advanced SSL encryption to protect your data.</p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
            <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-4">
              <i className="ri-verified-badge-fill text-2xl text-white"></i>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">Licensed Operators</h4>
            <p className="text-gray-600">We only feature casinos licensed by reputable gaming authorities.</p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-100">
            <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mb-4">
              <i className="ri-customer-service-2-fill text-2xl text-white"></i>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">24/7 Support</h4>
            <p className="text-gray-600">Get help anytime with our dedicated customer support team.</p>
          </div>
        </div>
      </div>
    </section>
  );
}